import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { Link, useParams } from 'react-router';
import { gql } from '@apollo/client';
import type { DocumentNode, ErrorLike } from '@apollo/client';
import { useApolloClient, useQuery } from '@apollo/client/react';
import { useMe } from '../lib/useMe';
import { mutationErrorMessage } from '../lib/gqlError';
import { formatDate, formatDateTime } from '../lib/format';
import { MENTIONABLE_USERS } from '../lib/access';
import type { AccessEvent, MentionUser } from '../lib/access';
import type { TaskAccessView, TaskAssignee } from '../lib/requestTypes';
import { DetailBody } from '../components/DetailRail';
import { useCrumb } from '../lib/crumb';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { ErrorBoundary } from '../components/ErrorBoundary';
import { Icon } from '../components/Icon';
import type { IconName } from '../components/Icon';
import { Markdown } from '../components/Markdown';
import { AttachProvider, STORED_FILE_FIELDS, seedFiles } from '../lib/files';
import type { StoredFile } from '../lib/files';
import { RichEditor } from '../components/RichEditor';
import { AssigneesPicker } from '../components/tasks/AssigneesPicker';
import type { AssigneeOption } from '../components/tasks/AssigneesPicker';
import { ActivityFeed } from '../components/requests/ActivityFeed';
import type { Tone } from '../components/requests/ActivityFeed';
import { CommentComposer } from '../components/requests/CommentComposer';
import { Person } from '../components/requests/AttributeSidebar';
import { AccessBox, useAccessPermissions } from '../components/requests/DecisionPanel';
import { ago, dueTone, fromDateInput, toDateInput } from '../lib/taskDates';
import { LabelChip } from '../components/labels/LabelChip';
import type { LabelLite } from '../components/labels/LabelChip';
import { LabelPicker } from '../components/labels/LabelPicker';
import '../styles/requests-shared.css';
import '../styles/requests-detail.css';
import '../styles/changes-detail.css';
import '../styles/request-detail.css';

/*
 * Task detail (/tasks/:id) — kept like a GitLab issue: people talk in comments.
 * Body: description, for an access task a compact access box (AccessBox), then the
 * activity (comments + history) with the composer at the bottom. Rail (one card, GitLab
 * sidebar order): Assignees · Labels · Due date ("Access until") · Board column · State ·
 * Created/Updated · Requester. Controls follow the server's caller-relative hints
 * (canEdit / canCancel / canReopen / canLabel) and, for the grant, the decide rule in
 * useAccessPermissions — the API re-checks every mutation.
 */

/* ── types (mirror apps/api/src/app/tasks/tasks.types.ts) ──────────────────── */

interface TaskLabel {
  id: number;
  name: string;
  scope: string | null;
  color: string;
  system: boolean;
  addedBy?: string | null;
  addedAt?: string | null;
  addedWeek?: string | null;
}
interface TaskEvent {
  id: number;
  kind: string;
  actorEmail: string;
  bodyMd?: string | null;
  meta: string;
  createdAt: string;
  editedAt?: string | null;
  /** VN week key of createdAt, e.g. 2026-W42. */
  weekKey: string;
}
interface AccessGrant {
  resourceType: string;
  entityId?: number | null;
  resourceName: string;
  status: string;
  decidedBy?: string | null;
  decisionNote?: string | null;
  decidedAt?: string | null;
  decisionNoteEditedAt?: string | null;
  decisionNoteEditedBy?: string | null;
  answers: { prompt: string; answer: string; sortOrder: number }[];
}
export interface TaskDetail {
  id: number;
  title: string;
  descriptionMd?: string | null;
  descriptionEditedAt?: string | null;
  descriptionEditedBy?: string | null;
  template: string;
  requesterEmail: string;
  assignees: TaskAssignee[];
  dueAt?: string | null;
  state: string;
  closedAt?: string | null;
  closedBy?: string | null;
  closeReason?: string | null;
  createdAt: string;
  updatedAt: string;
  labels: TaskLabel[];
  workflow?: string | null;
  access?: AccessGrant | null;
  events: TaskEvent[];
  canEdit: boolean;
  canCancel: boolean;
  canReopen: boolean;
  canLabel: boolean;
  /** Attachments referenced by the description / comments (signed links, ~15 min). */
  files?: StoredFile[] | null;
}

/* ── GraphQL ─────────────────────────────────────────────────────────────────── */

// Every mutation returns the fresh TaskDetail; the same fragment lets Apollo's
// normalised cache (TaskDetail:id) update the page without a refetch.
const TASK_DETAIL_FIELDS = gql`
  ${STORED_FILE_FIELDS}
  fragment TaskDetailFields on TaskDetail {
    id
    title
    descriptionMd
    descriptionEditedAt
    descriptionEditedBy
    template
    requesterEmail
    assignees {
      email
      assignedBy
      assignedAt
    }
    dueAt
    state
    closedAt
    closedBy
    closeReason
    createdAt
    updatedAt
    workflow
    canEdit
    canCancel
    canReopen
    canLabel
    labels {
      id
      name
      scope
      color
      system
      addedBy
      addedAt
      addedWeek
    }
    access {
      resourceType
      entityId
      resourceName
      status
      decidedBy
      decisionNote
      decidedAt
      decisionNoteEditedAt
      decisionNoteEditedBy
      answers {
        prompt
        answer
        sortOrder
      }
    }
    events {
      id
      kind
      actorEmail
      bodyMd
      meta
      createdAt
      editedAt
      weekKey
    }
    files {
      ...StoredFileFields
    }
  }
`;

const TASK = gql`
  ${TASK_DETAIL_FIELDS}
  query Task($id: Int!) {
    task(id: $id) {
      ...TaskDetailFields
    }
  }
`;

const ASSIGNABLE_USERS = gql`
  query TaskAssignableUsers {
    assignableUsers {
      email
      displayName
      role
    }
  }
`;

/** Access tasks: every staff person (any role) can be an assignee; new members may ask too. */
const ACCESS_ASSIGNABLE_USERS = gql`
  query TaskAccessAssignableUsers {
    accessAssignableUsers {
      email
      displayName
      role
    }
  }
`;

const mutationDoc = (sig: string, call: string) => gql`
  ${TASK_DETAIL_FIELDS}
  mutation ${sig} {
    ${call} {
      ...TaskDetailFields
    }
  }
`;
const UPDATE = mutationDoc('UpdateTask($id: Int!, $input: UpdateTaskInput!)', 'updateTask(id: $id, input: $input)');
const ADD_ASSIGNEE = mutationDoc(
  'AddTaskAssignee($id: Int!, $email: String!, $note: String)',
  'addTaskAssignee(id: $id, email: $email, note: $note)',
);
const REMOVE_ASSIGNEE = mutationDoc(
  'RemoveTaskAssignee($id: Int!, $email: String!, $note: String)',
  'removeTaskAssignee(id: $id, email: $email, note: $note)',
);
const CLOSE = mutationDoc('CloseTask($id: Int!, $reason: String!, $note: String)', 'closeTask(id: $id, reason: $reason, note: $note)');
const REOPEN = mutationDoc('ReopenTask($id: Int!, $note: String)', 'reopenTask(id: $id, note: $note)');
const CANCEL = mutationDoc('CancelTask($id: Int!, $note: String)', 'cancelTask(id: $id, note: $note)');
/** The State section's board-column control — the same move as a drag on the board (the API
 *  refuses `workflow::` labels through applyTaskLabel). */
const MOVE_ON_BOARD = mutationDoc(
  'MoveTaskOnBoardFromDetail($id: Int!, $to: String, $expectedFrom: String)',
  'moveTaskOnBoard(id: $id, to: $to, expectedFrom: $expectedFrom)',
);
/** Label pickers never offer workflow labels: the workflow column changes on the board. */
const NO_WORKFLOW_SCOPE = ['workflow'];
const isWorkflowLabel = (l: LabelLite) => /^workflow::/i.test(l.name);
const COMMENT = mutationDoc('AddTaskComment($id: Int!, $bodyMd: String!)', 'addTaskComment(id: $id, bodyMd: $bodyMd)');
const EDIT_COMMENT = mutationDoc(
  'EditTaskComment($eventId: Int!, $bodyMd: String!)',
  'editTaskComment(eventId: $eventId, bodyMd: $bodyMd)',
);
const DELETE_COMMENT = mutationDoc('DeleteTaskComment($eventId: Int!)', 'deleteTaskComment(eventId: $eventId)');
const DECIDE_ACCESS = mutationDoc(
  'DecideTaskAccess($id: Int!, $approve: Boolean!, $note: String!)',
  'decideTaskAccess(id: $id, approve: $approve, note: $note)',
);
const APPLY_LABEL = mutationDoc(
  'ApplyTaskLabel($id: Int!, $labelId: Int, $name: String)',
  'applyTaskLabel(id: $id, labelId: $labelId, name: $name)',
);
const REMOVE_LABEL = mutationDoc(
  'RemoveTaskLabel($id: Int!, $labelId: Int, $name: String)',
  'removeTaskLabel(id: $id, labelId: $labelId, name: $name)',
);

/* ── route id + error helpers (same approach as the old detail pages) ───────── */

/** Route ids are positive GraphQL Ints; anything else (`abc`, `0`, `1.5`, past 2^31-1) is not a task. */
function parseTaskId(raw: string): number | null {
  if (!/^[1-9]\d{0,9}$/.test(raw)) return null;
  const n = Number(raw);
  return n <= 2_147_483_647 ? n : null;
}

/** The api answers an unknown (or invisible) id with a NotFoundException carried in the GraphQL error. */
function isNotFoundError(error: unknown): boolean {
  const errs = (error as { errors?: unknown } | null)?.errors;
  if (!Array.isArray(errs)) return false;
  return errs.some((g) => {
    const ext = (g as { extensions?: Record<string, unknown> } | null)?.extensions ?? {};
    const orig = ext.originalError as { statusCode?: unknown } | undefined;
    return ext.status === 404 || orig?.statusCode === 404 || ext.code === 'NOT_FOUND';
  });
}

/* ── adapters onto the shared widgets ───────────────────────────────────────── */

const u = <T,>(v: T | null | undefined): T | undefined => (v == null ? undefined : v);

function toAccessEvents(events: TaskEvent[]): AccessEvent[] {
  return events.map((e) => ({
    id: String(e.id),
    kind: e.kind,
    actorEmail: e.actorEmail,
    note: u(e.bodyMd),
    meta: e.meta,
    createdAt: e.createdAt,
    editedAt: u(e.editedAt),
  }));
}

/** The access grant in the shape AccessBox / useAccessPermissions read. */
function toAccessView(r: TaskDetail): TaskAccessView | undefined {
  const a = r.access;
  if (!a) return undefined;
  return {
    id: String(r.id),
    requesterEmail: r.requesterEmail,
    resourceType: a.resourceType,
    entityId: a.entityId != null ? String(a.entityId) : undefined,
    resourceName: a.resourceName,
    status: a.status,
    decidedBy: u(a.decidedBy),
    decisionNote: u(a.decisionNote),
    decidedAt: u(a.decidedAt),
    decisionNoteEditedAt: u(a.decisionNoteEditedAt),
    decisionNoteEditedBy: u(a.decisionNoteEditedBy),
    dueAt: u(r.dueAt),
    createdAt: r.createdAt,
    assignees: r.assignees.map((x) => x.email.toLowerCase()),
    answers: a.answers,
  };
}

const toLite = (l: TaskLabel): LabelLite => ({
  id: String(l.id),
  name: l.name,
  color: l.color,
  scope: l.scope,
  system: l.system,
});

/* ── vocabulary ─────────────────────────────────────────────────────────────── */

const CLOSE_REASON_LABELS: Record<string, string> = {
  done: 'done',
  not_planned: 'not planned',
  cancelled: 'cancelled',
  denied: 'access denied',
};
const WORKFLOW_COLUMNS: Record<string, string> = { todo: 'TODO', 'in-progress': 'IN PROGRESS', done: 'DONE' };
/** 'workflow::in-progress' | 'in-progress' → 'IN PROGRESS'. */
const column = (labelName: unknown): string => {
  const v = typeof labelName === 'string' ? (labelName.split('::').pop() ?? labelName) : '';
  return WORKFLOW_COLUMNS[v] ?? v.toUpperCase();
};
/** '2026-W42' → 'W42'. */
const shortWeek = (wk?: string | null): string => (wk ? (wk.split('-').pop() ?? wk) : '');

function StateBadge({ r }: { r: Pick<TaskDetail, 'state' | 'closeReason'> }) {
  if (r.state === 'open')
    return (
      <span className="badge rq-state rq-state--open" data-testid="rq-state">
        Open
      </span>
    );
  const reason = r.closeReason ?? '';
  return (
    <span className={`badge rq-state ${reason === 'done' ? 'confirmed' : 'cr-cancelled'}`} data-testid="rq-state">
      Closed{reason ? ` · ${CLOSE_REASON_LABELS[reason] ?? reason}` : ''}
    </span>
  );
}

/* ── activity: access + change kinds, plus labels and close/reopen ──────────── */

const EVENT_LABELS: Record<string, string> = {
  created: 'created the task',
  edited: 'edited the task',
  reassigned: 'reassigned',
  due_changed: 'changed the due date',
  priority_changed: 'changed priority',
  linked: 'linked',
  unlinked: 'unlinked',
  checklist_added: 'added a checklist item',
  checklist_toggled: 'updated a checklist item',
  checklist_removed: 'removed a checklist item',
  decision_requested: 'asked for a decision',
  decision_made: 'recorded a decision',
  commented: 'commented',
  comment_edited: 'edited a comment',
  labeled: 'added a label',
  unlabeled: 'removed a label',
  closed: 'closed the task',
  reopened: 'reopened the task',
  attached: 'uploaded a file',
};

const FIELD_NAMES: Record<string, string> = {
  title: 'title',
  descriptionMd: 'description',
  priority: 'priority',
  dueAt: 'due date',
  decisionNote: 'decision note',
  checklist_order: 'checklist order',
};

function eventIcon(kind: string, meta: Record<string, unknown>): [IconName, Tone] | undefined {
  switch (kind) {
    case 'attached':
      return ['docs', ''];
    case 'reassigned':
      return ['users', ''];
    case 'due_changed':
      return ['clock', ''];
    case 'priority_changed':
      return ['alert', ''];
    case 'linked':
    case 'unlinked':
      return ['grid', ''];
    case 'checklist_toggled':
      return ['check', meta.done ? 'ok' : ''];
    case 'checklist_removed':
      return ['trash', ''];
    case 'decision_requested':
      return ['question', 'warn'];
    case 'decision_made':
      return ['check', 'ok'];
    case 'checklist_added':
    case 'edited':
    case 'comment_edited':
      return ['edit', ''];
    case 'labeled':
      return meta.scope === 'workflow' ? ['run', 'acc'] : ['catalog', ''];
    case 'unlabeled':
      return meta.scope === 'workflow' ? ['run', ''] : ['catalog', ''];
    case 'closed':
      return meta.reason === 'done' ? ['check', 'ok'] : ['close', 'warn'];
    case 'reopened':
      return ['refresh', 'acc'];
    default:
      // created / assigned / escalated / sign-off / decided / cancelled: the feed's own icons.
      return undefined;
  }
}

const str = (v: unknown): string | null => (typeof v === 'string' && v ? v : null);
function change(meta: Record<string, unknown>, field: string): { from: unknown; to: unknown } | null {
  const c = (meta.changes as Record<string, unknown> | undefined)?.[field] as { from?: unknown; to?: unknown } | undefined;
  return c && typeof c === 'object' ? { from: c.from, to: c.to } : null;
}
function parseMeta(s: string): Record<string, unknown> {
  try {
    const v: unknown = JSON.parse(s);
    return v && typeof v === 'object' ? (v as Record<string, unknown>) : {};
  } catch {
    return {};
  }
}

/**
 * A board move writes unlabeled + labeled sharing meta.moveId, and a scope swap writes
 * an unlabeled with reason 'scope_exclusive'. Show each as ONE line: drop the unlabeled
 * half when a labeled event of the same scope shares its moveId — the labeled line
 * names where it came from.
 */
function collapseMoves(events: TaskEvent[]): { shown: TaskEvent[]; fromOf: Map<number, string> } {
  const fromOf = new Map<number, string>();
  const drop = new Set<number>();
  const metas = new Map(events.map((e) => [e.id, parseMeta(e.meta)]));
  for (const e of events) {
    if (e.kind !== 'labeled') continue;
    const m = metas.get(e.id) ?? {};
    if (!m.moveId || !m.scope) continue;
    const pair = events.find((o) => {
      if (o.kind !== 'unlabeled' || drop.has(o.id)) return false;
      const om = metas.get(o.id) ?? {};
      return om.moveId === m.moveId && om.scope === m.scope;
    });
    if (pair) {
      drop.add(pair.id);
      const label = str(metas.get(pair.id)?.label);
      if (label) fromOf.set(e.id, label);
    }
  }
  return { shown: events.filter((e) => !drop.has(e.id)), fromOf };
}

function makeDescribe(r: TaskDetail, fromOf: Map<number, string>) {
  const weekOf = new Map(r.events.map((e) => [String(e.id), e.weekKey]));
  const colorOf = new Map(r.labels.map((l) => [l.name.toLowerCase(), l.color]));
  const muted = (t: string) => <span className="muted">{t}</span>;
  const chip = (name: string | null, scope: unknown) =>
    name ? (
      <LabelChip
        size="sm"
        label={{
          id: name,
          name,
          color: colorOf.get(name.toLowerCase()) ?? 'gray',
          scope: typeof scope === 'string' ? scope : null,
        }}
      />
    ) : (
      <strong>a label</strong>
    );
  const wk = (e: AccessEvent) => {
    const w = shortWeek(weekOf.get(e.id));
    return w ? <span className="muted rq-week"> · {w}</span> : null;
  };
  const onBoard = (m: Record<string, unknown>) => (m.via === 'board' ? muted(' on the board') : null);

  return function describe(e: AccessEvent, meta: Record<string, unknown>): ReactNode | undefined {
    switch (e.kind) {
      case 'attached':
        return (
          <>
            {muted('uploaded')} <strong data-testid="tl-attached-name">{str(meta.filename) ?? 'a file'}</strong>
          </>
        );
      case 'labeled': {
        const label = str(meta.label);
        const from = fromOf.get(Number(e.id));
        if (meta.scope === 'workflow') {
          return (
            <>
              {muted('moved to')} <strong>{column(label)}</strong>
              {from && (
                <>
                  {' '}
                  {muted('from')} {column(from)}
                </>
              )}
              {wk(e)}
            </>
          );
        }
        return (
          <>
            {muted('added')} {chip(label, meta.scope)}
            {from && (
              <>
                {' '}
                {muted('replacing')} {chip(from, meta.scope)}
              </>
            )}
          </>
        );
      }
      case 'unlabeled': {
        const label = str(meta.label);
        const reason = str(meta.reason);
        if (meta.scope === 'workflow') {
          return (
            <>
              {muted('took it off the board from')} <strong>{column(label)}</strong>
              {reason && reason !== 'scope_exclusive' && muted(` — ${CLOSE_REASON_LABELS[reason] ?? reason}`)}
              {wk(e)}
            </>
          );
        }
        return (
          <>
            {muted('removed')} {chip(label, meta.scope)}
            {reason === 'scope_exclusive' && muted(' (one label per scope)')}
          </>
        );
      }
      case 'closed': {
        const reason = str(meta.reason) ?? '';
        return (
          <>
            {muted('closed it as')} <strong>{CLOSE_REASON_LABELS[reason] ?? (reason || 'closed')}</strong>
            {meta.via === 'decision' && muted(' (access decision)')}
            {onBoard(meta)}
            {wk(e)}
          </>
        );
      }
      case 'reopened':
        return (
          <>
            {muted('reopened it')}
            {onBoard(meta)}
            {wk(e)}
          </>
        );
      case 'reassigned': {
        const to = str(meta.to);
        return (
          <>
            {muted('reassigned to')} {to ? <span className="cr-handoff">{to}</span> : <strong>nobody</strong>}
          </>
        );
      }
      case 'assignee_added':
      case 'assignee_removed': {
        const who = str(meta.email);
        return (
          <>
            {muted(e.kind === 'assignee_added' ? 'assigned' : 'unassigned')}{' '}
            {who ? <strong>{who}</strong> : muted('someone')}
            {wk(e)}
          </>
        );
      }
      case 'due_changed': {
        const to = str(change(meta, 'dueAt')?.to);
        return to ? (
          <>
            {muted('set the due date to')} <strong>{formatDate(to)}</strong>
          </>
        ) : (
          muted('cleared the due date')
        );
      }
      case 'priority_changed': {
        const c = change(meta, 'priority');
        const from = str(c?.from);
        const to = str(c?.to);
        return (
          <>
            {muted('changed priority')} {from ? <strong>{from.toUpperCase()}</strong> : muted('none')} →{' '}
            {to ? <strong>{to.toUpperCase()}</strong> : muted('none')}
          </>
        );
      }
      case 'edited': {
        const fields = Array.isArray(meta.fields) ? meta.fields.map(String) : [];
        if (fields.length === 1 && fields[0] === 'checklist_order') return muted('reordered the checklist');
        return muted(`edited the ${fields.map((f) => FIELD_NAMES[f] ?? f).join(', ') || 'task'}`);
      }
      case 'linked':
      case 'unlinked': {
        const what = meta.redacted ? 'a restricted secret' : `${String(meta.entityType ?? '')} ${str(meta.name) ?? ''}`.trim();
        return (
          <>
            {muted(e.kind)} <strong>{what}</strong>
          </>
        );
      }
      case 'checklist_added':
        return (
          <>
            {muted(`added a ${meta.section === 'verify' ? 'verify' : 'plan'} item`)} <span>{str(meta.text)}</span>
          </>
        );
      case 'checklist_toggled':
        return (
          <>
            {muted(meta.done ? 'ticked' : 'unticked')} <span>{str(meta.text)}</span>
          </>
        );
      case 'checklist_removed':
        return (
          <>
            {muted('removed')} <span>{str(meta.text)}</span>
          </>
        );
      case 'decision_made':
        return (
          <>
            {muted('recorded decision')} — <strong>{str(meta.chosen)}</strong>
          </>
        );
      case 'decided': {
        // The decision also closes the task: say so (approved → done, denied → not planned).
        const cr = str(meta.closeReason);
        if (cr !== 'done' && cr !== 'not_planned') return undefined;
        return (
          <>
            decided
            {meta.status ? (
              <>
                {' '}
                — <strong>{String(meta.status)}</strong>
              </>
            ) : null}
            {meta.selfDecided ? (
              <>
                {' '}
                <span className="badge warn" title="Decided by the requester (root) — no second reviewer">
                  self-decided
                </span>
              </>
            ) : null}
            {muted(` · closed it as ${cr === 'done' ? 'done' : 'not planned'} (access decision)`)}
          </>
        );
      }
      default:
        // Access kinds (created, assigned, escalated, sign-off, cancelled): the feed's rendering —
        // escalated / maintainer sign-off are history from the layered review that was removed.
        return undefined;
    }
  };
}

/* ── the page ────────────────────────────────────────────────────────────────── */

type EditField = 'title' | 'descriptionMd';
type CloseReason = 'done' | 'not_planned' | 'cancelled';
type RailField = 'assignee' | 'due' | 'labels' | 'state';
/** Where a refusal is shown inline: a rail field, or one of the body editors. */
type ErrField = RailField | EditField | 'comment';

export default function TaskDetailPage() {
  const { id = '' } = useParams();
  const { me, loading: meLoading } = useMe();
  const client = useApolloClient();
  const taskId = parseTaskId(id);
  // Breadcrumb tail: "Tasks / #225" (the title is right below it).
  useCrumb(taskId != null ? `#${taskId}` : null);
  const isNewMember = me?.role === 'new_member';

  const { data, loading, error, refetch } = useQuery<{ task: TaskDetail }>(TASK, {
    variables: { id: taskId ?? 0 },
    skip: taskId == null,
    fetchPolicy: 'cache-and-network',
  });
  const r = data?.task;
  const isAccess = !!r?.access;
  // Markdown resolves `infras-file:` refs through a cache; the task payload already carries them.
  useEffect(() => seedFiles(r?.files), [r?.files]);
  const attachTarget = useMemo(() => (r ? { taskId: r.id } : null), [r?.id]);
  const [descUploading, setDescUploading] = useState(false);
  const { data: mentionData } = useQuery<{ mentionableUsers: MentionUser[] }>(MENTIONABLE_USERS, {
    skip: !me,
    fetchPolicy: 'cache-and-network',
  });
  type DirUser = { email: string; displayName?: string | null; role?: string | null };
  const { data: peopleData } = useQuery<{ assignableUsers: DirUser[] }>(ASSIGNABLE_USERS, {
    skip: !me || !r || isAccess,
    fetchPolicy: 'cache-and-network',
  });
  const { data: accessPeopleData } = useQuery<{ accessAssignableUsers: DirUser[] }>(ACCESS_ASSIGNABLE_USERS, {
    skip: !me || !isAccess,
    fetchPolicy: 'cache-and-network',
  });
  const mentionUsers = mentionData?.mentionableUsers ?? [];
  // Plain task: engineer or above. Access task: any staff person except the requester.
  const requesterLc = (r?.requesterEmail ?? '').toLowerCase();
  const people: AssigneeOption[] = isAccess
    ? (accessPeopleData?.accessAssignableUsers ?? [])
        .filter((x) => x.email.toLowerCase() !== requesterLc)
        .map((x) => ({ email: x.email.toLowerCase(), label: x.displayName || x.email }))
    : (peopleData?.assignableUsers ?? [])
        .filter((x) => x.role && x.role !== 'new_member')
        .map((x) => ({ email: x.email.toLowerCase(), label: x.displayName || x.email }));

  const [mutErr, setMutErr] = useState<string | null>(null);
  const [fieldErr, setFieldErr] = useState<{ field: ErrField; msg: string } | null>(null);
  const [busy, setBusy] = useState(false);
  // access grant: the decision note (required to approve or deny)
  const [note, setNote] = useState('');
  // body edits
  const [editing, setEditing] = useState<EditField | null>(null);
  const [draft, setDraft] = useState('');
  // comments
  const [comment, setComment] = useState('');
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [commentDraft, setCommentDraft] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  // rail
  const [closing, setClosing] = useState(false);
  const [closeReason, setCloseReason] = useState<CloseReason>('done');
  const [closeNote, setCloseNote] = useState('');
  const [editDue, setEditDue] = useState(false);
  const [dueDraft, setDueDraft] = useState('');
  // confirmations
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [copied, setCopied] = useState(false);

  const ar = r ? toAccessView(r) : undefined;
  // Decide: any assignee (any role) or root/maintainer, never the requester (root self-decide
  // is marked); cancel follows the server's canCancel.
  const p = useAccessPermissions(ar, me, note, !!r?.canCancel);

  /**
   * Run one mutation. Success: the returned detail lands in the cache (same fragment).
   * Failure: show the API's message (next to the rail field when one is named) and
   * refetch — a refusal usually means the ticket moved on under us.
   */
  const act = async (mutation: DocumentNode, variables: Record<string, unknown>, field?: ErrField): Promise<boolean> => {
    setBusy(true);
    try {
      await client.mutate({ mutation, variables });
      setMutErr(null);
      setFieldErr(null);
      return true;
    } catch (e) {
      const msg = mutationErrorMessage(e as ErrorLike) ?? (e as Error).message ?? 'Update failed';
      if (field) {
        setFieldErr({ field, msg });
        setMutErr(null);
      } else {
        setFieldErr(null);
        setMutErr(msg);
      }
      void refetch();
      return false;
    } finally {
      setBusy(false);
    }
  };

  if (meLoading || (loading && !data)) return <div className="page">Loading…</div>;
  if (error && !isNotFoundError(error) && !r) return <div className="page muted">Failed to load: {error.message}</div>;
  if (taskId == null || !r)
    return (
      <div className="page" data-testid="rq-not-found">
        <p className="muted">Task not found.</p>
        <Link to="/tasks">← Back to tasks</Link>
      </div>
    );

  const myEmail = p.myEmail;
  const vars = { id: r.id };
  const closed = r.state === 'closed';
  const labels = r.labels.map(toLite);
  const grantPending = r.access?.status === 'pending';
  // Description: an access task keeps its rule (the requester corrects their own
  // reason); any other task follows the server's canEdit (requester | assignee | root, open).
  const canEditDesc = isAccess ? p.isRequester && r.canEdit : r.canEdit;
  const due = r.dueAt ? dueTone(r.dueAt, closed) : null;
  const { shown, fromOf } = collapseMoves(r.events);
  const describe = makeDescribe(r, fromOf);
  // While an access grant is pending only Cancel closes the task (the API refuses done /
  // not planned): the grant becomes cancelled with it.
  const canOpenClose = grantPending ? r.canCancel : r.canEdit || r.canCancel;

  const startEdit = (f: EditField) => {
    setFieldErr(null);
    setEditing(f);
    setDraft((f === 'title' ? r.title : r.descriptionMd) ?? '');
  };
  const cancelEdit = () => {
    if (busy) return;
    setFieldErr(null);
    setEditing(null);
  };
  const saveEdit = () => {
    if (!editing || busy) return;
    if (editing === 'descriptionMd' && descUploading) return;
    const v = draft.trim();
    if (editing === 'title' && !v) return;
    // Unchanged → just close: the API refuses an update with nothing to change.
    if (v === ((editing === 'title' ? r.title : r.descriptionMd) ?? '').trim()) return cancelEdit();
    void act(UPDATE, { ...vars, input: { [editing]: v || null } }, editing).then((ok) => ok && setEditing(null));
  };
  const cancelCommentEdit = () => {
    if (busy) return;
    setFieldErr(null);
    setEditingCommentId(null);
    setCommentDraft('');
  };
  const saveCommentEdit = (e: { id: string; note?: string | null }) => {
    const v = commentDraft.trim();
    if (busy || !v) return;
    if (v === (e.note ?? '').trim()) return cancelCommentEdit();
    void act(EDIT_COMMENT, { eventId: Number(e.id), bodyMd: v }, 'comment').then((ok) => {
      if (!ok) return;
      setEditingCommentId(null);
      setCommentDraft('');
    });
  };
  const submitComment = () => {
    if (busy || !comment.trim()) return;
    void act(COMMENT, { ...vars, bodyMd: comment.trim() }).then((ok) => ok && setComment(''));
  };
  const openClose = () => {
    setCloseReason(!grantPending && r.canEdit ? 'done' : 'cancelled');
    setCloseNote('');
    setFieldErr(null);
    setClosing(true);
  };
  const submitClose = () => {
    const n = closeNote.trim() || null;
    const run =
      closeReason === 'cancelled'
        ? act(CANCEL, { ...vars, note: n }, 'state')
        : act(CLOSE, { ...vars, reason: closeReason, note: n }, 'state');
    void run.then((ok) => {
      if (!ok) return;
      setClosing(false);
      setCloseNote('');
    });
  };
  const saveDue = () => {
    // Same rule as the new form: today or later (the API allows at most 1 day back).
    if (dueDraft && dueDraft < toDateInput(new Date().toISOString())) {
      setFieldErr({ field: 'due', msg: 'Pick today or a later date.' });
      return;
    }
    void act(UPDATE, { ...vars, input: { dueAt: fromDateInput(dueDraft) } }, 'due').then((ok) => ok && setEditDue(false));
  };
  const fieldMsg = (f: ErrField) =>
    fieldErr?.field === f ? (
      <p className="error cr-field-error" role="alert" data-testid={`rq-${f}-error`}>
        {fieldErr.msg}
      </p>
    ) : null;

  const assigneeEmails = r.assignees.map((a: TaskAssignee) => a.email.toLowerCase());
  // Who can change the assignees: the server's general-edit rule (requester | assignee |
  // root, open). A new member only on their OWN task (the API checks ownership);
  // on anyone else's task they see the chips read-only.
  // p.isRequester only exists for an access task; a plain task's requester is read off the row.
  const iFiled = !!me && r.requesterEmail.toLowerCase() === me.email.toLowerCase();
  const canAssign = r.canEdit && (!isNewMember || iFiled);
  // The task's ONE date: a deadline, or for an access task the grant expiry (empty = permanent).
  const dueLabel = isAccess ? 'Access until' : 'Due date';

  // Workflow labels are not in the picker (they move on the board); the rail shows them read-only.
  // canLabel is true on a closed task too (plain labels stay editable); the board stays open-only.
  const boardShown = r.canLabel && !closed && !closing;
  /** One rail card, GitLab sidebar order. No value appears twice. */
  const rail = (
    <section className="rail-card rq-rail-card" data-testid="rq-rail">
      <div className="rq-rail-sec" data-testid="rq-assigned">
        <h4 className="rail-card__title">Assignees</h4>
        {canAssign ? (
          <AssigneesPicker
            id="rq-assignees"
            ariaLabel="Assignees"
            value={assigneeEmails}
            options={people}
            disabled={busy}
            onAdd={async (email) => {
              await act(ADD_ASSIGNEE, { ...vars, email, note: null }, 'assignee');
            }}
            onRemove={async (email) => {
              await act(REMOVE_ASSIGNEE, { ...vars, email, note: null }, 'assignee');
            }}
          />
        ) : (
          <div className="rq-people" data-testid="rq-assignees-ro">
            {assigneeEmails.length ? (
              assigneeEmails.map((x) => <Person key={x} email={x} you={x === myEmail} />)
            ) : (
              <span className="muted">Unassigned</span>
            )}
          </div>
        )}
        {fieldMsg('assignee')}
      </div>

      {/* Labels live only here (not under the title). rq-labels = every applied label, workflow included. */}
      <div className="rq-rail-sec" data-testid={labels.length ? 'rq-labels' : undefined}>
        <h4 className="rail-card__title">Labels</h4>
        {/* The picker renders the applied chips itself; read-only viewers get a plain chip row. */}
        {!r.canLabel && (
          <div className="rq-labels" data-testid="rq-rail-labels">
            {labels.length ? labels.map((l) => <LabelChip key={l.id} label={l} />) : <span className="muted">No labels</span>}
          </div>
        )}
        {r.canLabel && labels.some(isWorkflowLabel) && (
          <div className="rq-labels" data-testid="rq-rail-workflow">
            {labels.filter(isWorkflowLabel).map((l) => (
              <LabelChip key={l.id} label={l} size="sm" />
            ))}
          </div>
        )}
        {r.canLabel && (
          <div data-testid="rq-label-picker">
            <LabelPicker
              value={labels.filter((l) => !isWorkflowLabel(l))}
              disabled={busy}
              excludeScopes={NO_WORKFLOW_SCOPE}
              onApply={async (l) => {
                await act(APPLY_LABEL, { ...vars, labelId: Number(l.id) }, 'labels');
              }}
              onRemove={async (l) => {
                await act(REMOVE_LABEL, { ...vars, labelId: Number(l.id) }, 'labels');
              }}
            />
          </div>
        )}
        {fieldMsg('labels')}
      </div>

      <div className="rq-rail-sec">
        <div className="rq-rail-head">
          <h4 className="rail-card__title">{dueLabel}</h4>
          {r.canEdit && !editDue && (
            <button
              type="button"
              className="mini-btn"
              disabled={busy}
              aria-label={`Edit ${dueLabel.toLowerCase()}`}
              onClick={() => {
                setDueDraft(toDateInput(r.dueAt));
                setFieldErr(null);
                setEditDue(true);
              }}
            >
              Edit
            </button>
          )}
        </div>
        <div data-testid="rq-due">
          {editDue ? (
            <div className="cr-rail-edit rq-due-edit">
              <input
                type="date"
                aria-label={dueLabel}
                value={dueDraft}
                min={toDateInput(new Date().toISOString())}
                disabled={busy}
                onChange={(e) => setDueDraft(e.target.value)}
              />
              <button type="button" className="mini-btn" disabled={busy || !dueDraft} onClick={saveDue}>
                Save
              </button>
              {r.dueAt && (
                <button
                  type="button"
                  className="mini-btn"
                  disabled={busy}
                  onClick={() =>
                    void act(UPDATE, { ...vars, input: { dueAt: null } }, 'due').then((ok) => ok && setEditDue(false))
                  }
                >
                  Clear
                </button>
              )}
              <button type="button" className="mini-btn" disabled={busy} onClick={() => setEditDue(false)}>
                Cancel
              </button>
            </div>
          ) : (
            <span className="cr-rail-v">
              {r.dueAt ? (
                <>
                  {formatDate(r.dueAt)}
                  {due?.text && <span className={due.cls}>· {due.text}</span>}
                </>
              ) : (
                <span className="muted">{isAccess ? 'Permanent' : 'No due date'}</span>
              )}
            </span>
          )}
          {editDue && fieldMsg('due')}
        </div>
      </div>

      {boardShown && (
        <div className="rq-rail-sec">
          <h4 className="rail-card__title">Board column</h4>
          <label className="rq-rail-ctl" data-testid="rq-board-column">
            <select
              className="class-select class-select--sm"
              value={r.workflow ?? ''}
              disabled={busy}
              aria-label="Board column"
              onChange={(e) => {
                const to = e.target.value || null;
                void act(MOVE_ON_BOARD, { ...vars, to, expectedFrom: r.workflow ?? null }, 'state');
              }}
            >
              <option value="">No workflow label</option>
              <option value="todo">{column('todo')}</option>
              <option value="in-progress">{column('in-progress')}</option>
              <option value="done" disabled={grantPending}>
                {column('done')}
                {grantPending ? ' (not while the access grant is pending)' : ''}
              </option>
            </select>
          </label>
        </div>
      )}

      {(closed || canOpenClose || fieldErr?.field === 'state') && (
      <div className="rq-rail-sec" data-testid="rq-rail-state">
        <h4 className="rail-card__title">State</h4>
        {closed && r.closedAt && (
          <p className="muted text-xs rq-rail-note" data-testid="rq-closed-by">
            Closed {r.closedBy ? `by ${r.closedBy} · ` : ''}
            {ago(r.closedAt)}
          </p>
        )}
        {!closed && canOpenClose && !closing && (
          <div className="cr-rail-edit">
            <button type="button" className="mini-btn" disabled={busy} data-testid="rq-close-open" onClick={openClose}>
              Close…
            </button>
          </div>
        )}
        {closing && !closed && (
          <form
            className="rq-close"
            data-testid="rq-close-form"
            onSubmit={(e) => {
              e.preventDefault();
              submitClose();
            }}
          >
            <fieldset className="rq-close__reasons" disabled={busy}>
              <legend className="muted text-xs">Close as</legend>
              {grantPending && (
                <p className="muted text-xs" data-testid="rq-close-pending-note">
                  The access grant is still pending, so this task can only be cancelled — cancelling
                  also cancels the pending access.
                </p>
              )}
              {r.canEdit && !grantPending && (
                <label className="rq-close__opt">
                  <input
                    type="radio"
                    name="close-reason"
                    value="done"
                    checked={closeReason === 'done'}
                    onChange={() => setCloseReason('done')}
                  />
                  <span>Done</span>
                </label>
              )}
              {r.canEdit && !grantPending && (
                <label className="rq-close__opt">
                  <input
                    type="radio"
                    name="close-reason"
                    value="not_planned"
                    checked={closeReason === 'not_planned'}
                    onChange={() => setCloseReason('not_planned')}
                  />
                  <span>Not planned</span>
                </label>
              )}
              {r.canCancel && (
                <label className="rq-close__opt">
                  <input
                    type="radio"
                    name="close-reason"
                    value="cancelled"
                    checked={closeReason === 'cancelled'}
                    onChange={() => setCloseReason('cancelled')}
                  />
                  <span>
                    Cancelled
                    {grantPending && <span className="muted text-xs"> — cancels the pending access</span>}
                  </span>
                </label>
              )}
            </fieldset>
            <textarea
              className="rq-close__note"
              aria-label="Close note (optional)"
              placeholder="Note (optional)"
              rows={2}
              value={closeNote}
              disabled={busy}
              onChange={(e) => setCloseNote(e.target.value)}
            />
            <div className="btn-row">
              <button type="submit" className="btn btn-primary btn-sm" disabled={busy} data-testid="rq-close-submit">
                Close task
              </button>
              <button type="button" className="btn btn-ghost btn-sm" disabled={busy} onClick={() => setClosing(false)}>
                Keep open
              </button>
            </div>
          </form>
        )}
        {closed && r.canReopen && (
          <div className="cr-rail-edit">
            <button
              type="button"
              className="mini-btn"
              disabled={busy}
              data-testid="rq-reopen"
              onClick={() => void act(REOPEN, { ...vars, note: null }, 'state')}
            >
              Reopen
            </button>
          </div>
        )}
        {fieldMsg('state')}
      </div>
      )}

      <div className="rq-rail-sec">
        <div className="cr-attrs">
          <div className="cr-attrs__row">
            <div className="cr-attrs__k">Created</div>
            <div className="cr-attrs__v muted">{formatDateTime(r.createdAt)}</div>
          </div>
          <div className="cr-attrs__row">
            <div className="cr-attrs__k">Updated</div>
            <div className="cr-attrs__v muted">{ago(r.updatedAt)}</div>
          </div>
          <div className="cr-attrs__row">
            <div className="cr-attrs__k">Requester</div>
            <div className="cr-attrs__v rq-rail-person" title={r.requesterEmail}>
              <Person email={r.requesterEmail} you={r.requesterEmail.toLowerCase() === myEmail} oneLine />
            </div>
          </div>
        </div>
      </div>
    </section>
  );

  return (
    <div className="page page--wide req-detail cr-detail rq-detail" data-testid="rq-detail">
      <div className="detail-back">
        <Link to="/tasks" className="muted">
          ← Tasks
        </Link>
      </div>

      <div className="detail-title">
        {editing === 'title' ? (
          <>
            <form
              className="cr-title-edit"
              onSubmit={(e) => {
                e.preventDefault();
                saveEdit();
              }}
            >
              <label htmlFor="rq-title-input" className="cr-head__id">
                #{r.id} title
              </label>
              <input
                id="rq-title-input"
                value={draft}
                disabled={busy}
                autoFocus
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => e.key === 'Escape' && cancelEdit()}
              />
              <button type="submit" className="btn btn-primary" disabled={busy || !draft.trim()}>
                Save
              </button>
              <button type="button" className="btn btn-ghost" disabled={busy} onClick={cancelEdit}>
                Cancel
              </button>
            </form>
            {fieldMsg('title')}
          </>
        ) : (
          <div className="detail-title__row">
            <h2 className="detail-title__name" data-testid="rq-title">
              <span className="cr-head__id">#{r.id} · </span>
              {r.title}
            </h2>
            <div className="rq-head-actions">
              {r.canEdit && (
                <button type="button" className="mini-btn" disabled={busy || editing != null} onClick={() => startEdit('title')}>
                  <Icon name="edit" className="req-ico" />
                  Edit title
                </button>
              )}
              <button
                type="button"
                className="mini-btn"
                onClick={() => {
                  void navigator.clipboard?.writeText(window.location.href).then(
                    () => {
                      setCopied(true);
                      window.setTimeout(() => setCopied(false), 1200);
                    },
                    () => undefined,
                  );
                }}
              >
                <Icon name={copied ? 'check' : 'copy'} className="req-ico" />
                {copied ? 'Copied' : 'Copy link'}
              </button>
            </div>
          </div>
        )}
        <div className="cr-head__row">
          <StateBadge r={r} />
          {isAccess && (
            <span className="cr-kind" data-testid="rq-template">
              <Icon name="access" size={13} className="req-ico" />
              Access
            </span>
          )}
        </div>
      </div>

      {error && r && !isNotFoundError(error) && (
        <p className="error" role="alert" data-testid="rq-load-error">
          Could not refresh this task: {error.message} — showing the last loaded version.
        </p>
      )}
      {mutErr && (
        <p className="error" role="alert" data-testid="rq-error">
          {mutErr}
        </p>
      )}

      <AttachProvider value={attachTarget ?? { taskId: r.id }}>
      <DetailBody rail={rail}>
        <section>
          <h3 className="section">Description</h3>
          {editing === 'descriptionMd' ? (
            <div className="rq-desc-edit" data-testid="rq-desc-edit">
              <ErrorBoundary fallback={(m) => <p className="error">editor failed: {m}</p>}>
                <RichEditor
                  value={draft}
                  onChange={setDraft}
                  placeholder={isAccess ? 'Describe what you need access to and why…' : 'Describe the task…'}
                  disabled={busy}
                  mentionUsers={mentionUsers}
                  autoFocus
                  onSubmit={saveEdit}
                  onCancel={cancelEdit}
                  onUploadingChange={setDescUploading}
                />
              </ErrorBoundary>
              {fieldMsg('descriptionMd')}
              <div className="btn-row rq-gap-top">
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={busy || descUploading}
                  title={descUploading ? 'Wait for the upload to finish' : undefined}
                  onClick={saveEdit}
                >
                  Save description
                </button>
                <button type="button" className="btn btn-ghost" disabled={busy} onClick={cancelEdit}>
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="cr-prose" data-testid="rq-description">
              {r.descriptionMd ? (
                <Markdown>{r.descriptionMd}</Markdown>
              ) : (
                <p className="cr-prose--empty">No description provided.</p>
              )}
              {(r.descriptionEditedAt || canEditDesc) && (
                <div className="cr-prose__foot">
                  {r.descriptionEditedAt && (
                    <span className="muted text-xs">
                      edited {formatDateTime(r.descriptionEditedAt)}
                      {r.descriptionEditedBy ? ` by ${r.descriptionEditedBy}` : ''}
                    </span>
                  )}
                  {canEditDesc && (
                    <button
                      type="button"
                      className="mini-btn"
                      disabled={busy || editing != null}
                      aria-label="Edit description"
                      onClick={() => startEdit('descriptionMd')}
                    >
                      <Icon name="edit" className="req-ico" />
                      Edit
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </section>

        {ar && (
          <div data-testid="rq-access">
            <AccessBox
              r={ar}
              p={p}
              busy={busy}
              mentionUsers={mentionUsers}
              note={note}
              setNote={setNote}
              onDecide={(approve) =>
                void act(DECIDE_ACCESS, { ...vars, approve, note: note.trim() }).then((ok) => ok && setNote(''))
              }
              onAskCancel={() => setConfirmCancel(true)}
            />
          </div>
        )}

        <div data-testid="rq-activity">
          <ActivityFeed
            events={toAccessEvents(shown)}
            myEmail={myEmail}
            busy={busy}
            mentionUsers={mentionUsers}
            canDeleteAny={me?.role === 'root'}
            labels={EVENT_LABELS}
            decidedStatus={ar?.status ?? null}
            iconFor={eventIcon}
            describe={describe}
            editingCommentId={editingCommentId}
            commentDraft={commentDraft}
            setCommentDraft={setCommentDraft}
            onStartEdit={(e) => {
              setFieldErr(null);
              setEditingCommentId(e.id);
              setCommentDraft(e.note ?? '');
            }}
            onCancelEdit={cancelCommentEdit}
            onSaveEdit={saveCommentEdit}
            editError={fieldErr?.field === 'comment' ? fieldErr.msg : null}
            onAskDelete={(e) => setDeleteTarget(e.id)}
          />
          <CommentComposer
            value={comment}
            onChange={setComment}
            onSubmit={submitComment}
            busy={busy}
            mentionUsers={mentionUsers}
            myEmail={myEmail}
            showDecisionHint={isAccess}
          />
        </div>
      </DetailBody>
      </AttachProvider>

      <ConfirmDialog
        open={confirmCancel}
        title={iFiled ? 'Cancel this task?' : `Cancel ${r.requesterEmail}'s task?`}
        body="It stays in the system, closed as cancelled — the timeline is kept. The requester is emailed."
        confirmLabel="Cancel task"
        cancelLabel="Keep it open"
        danger
        busy={busy}
        onConfirm={() => {
          setConfirmCancel(false);
          void act(CANCEL, { ...vars, note: null });
        }}
        onCancel={() => setConfirmCancel(false)}
      />
      <ConfirmDialog
        open={deleteTarget != null}
        title="Delete this comment?"
        body="It is removed from the timeline for everyone. This cannot be undone."
        confirmLabel="Delete comment"
        danger
        busy={busy}
        onConfirm={() => {
          const t = deleteTarget;
          setDeleteTarget(null);
          if (t != null) void act(DELETE_COMMENT, { eventId: Number(t) });
        }}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
